import React from 'react';
import { X, AlertTriangle, HelpCircle } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import { pendingFemaLabel, PENDING_FEMA_COLOR } from '../lib/fema-pending';

export interface FloodZoneProperties {
  id: string;
  zone_code: string;
  flood_type: string;
  metadata: { zone_subtype?: string; sfha?: string; static_bfe?: number } | null;
  source?: 'fema_pending';
  product_id?: string;
  effective_date?: string;
  source_url?: string;
}

interface FloodZoneDetailProps {
  floodZone: FloodZoneProperties;
  onClose: () => void;
}

const ZONE_INFO: Record<string, { name: string; description: string }> = {
  A:   { name: 'Zone A',   description: '1% annual chance of flooding. No base flood elevations determined. Detailed hydraulic analysis not performed.' },
  AE:  { name: 'Zone AE',  description: '1% annual chance of flooding. Base flood elevations determined by detailed hydraulic analysis.' },
  AH:  { name: 'Zone AH',  description: '1% annual chance of shallow flooding (ponding), usually 1\u20133 ft deep. Base flood elevations determined.' },
  AO:  { name: 'Zone AO',  description: '1% annual chance of shallow flooding (sheet flow on sloping terrain), usually 1\u20133 ft deep.' },
  A99: { name: 'Zone A99', description: '1% annual chance of flooding, but protected by a federal flood protection system under construction.' },
  AR:  { name: 'Zone AR',  description: '1% annual chance of flooding where a previously accredited flood protection system is being restored.' },
  VE:  { name: 'Zone VE',  description: 'Coastal flood zone with velocity hazard (wave action). 1% annual chance. Base flood elevations determined.' },
  V:   { name: 'Zone V',   description: 'Coastal flood zone with velocity hazard (wave action). 1% annual chance. No base flood elevations.' },
  D:   { name: 'Zone D',   description: 'Unstudied area where flood hazards are undetermined. No analysis has been performed.' },
};

export const FloodZoneDetail: React.FC<FloodZoneDetailProps> = ({ floodZone, onClose }) => {
  const { theme } = useTheme();
  const fz = floodZone;
  const is100yr = fz.flood_type === '100yr';
  const pending = fz.source === 'fema_pending';
  const info = ZONE_INFO[fz.zone_code] || { name: `Zone ${fz.zone_code}`, description: 'FEMA flood hazard zone.' };
  const accentColor = pending ? PENDING_FEMA_COLOR : is100yr ? '#dc2626' : '#f97316';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} color={accentColor} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Flood Zone</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Risk badge */}
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: accentColor + '15', border: `1px solid ${accentColor}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: accentColor, marginBottom: '4px' }}>
            {info.name}
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            {info.description}
          </div>
        </div>

        {/* Classification */}
        <Section title="Classification">
          {pending && <div style={{ color: accentColor, fontSize: 12, marginBottom: 10 }}>{pendingFemaLabel(fz.effective_date)}</div>}
          <Field label="Zone Code" value={fz.zone_code} />
          <Field label="Risk Level" value={is100yr ? 'High Risk (SFHA)' : fz.flood_type === '500yr' ? '0.2% annual chance' : 'Undetermined'} />
          <Field label="SFHA" value={fz.metadata?.sfha === 'T' ? 'Yes' : fz.metadata?.sfha === 'F' ? 'No' : null} />
          <Field label="Subtype" value={fz.metadata?.zone_subtype || null} />
          {fz.metadata?.static_bfe != null && (
            <Field label="Base Flood Elev." value={`${fz.metadata.static_bfe} ft`} />
          )}
        </Section>

        {/* What this means for development */}
        <Section title="Development Impact">
          {pending ? (
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
              This published mapping is pending its effective date. Use the mapped hazard for future site planning; it does not establish the current effective flood-zone determination or current insurance requirements.
            </div>
          ) : is100yr ? (
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
              <div style={{ marginBottom: '10px' }}>
                Land in a <strong>Special Flood Hazard Area (SFHA)</strong> carries regulatory and financial consequences:
              </div>
              <ImpactItem
                label="Insurance Required"
                detail="Federally-backed mortgages require flood insurance (NFIP). Commercial policies typically $2K\u2013$20K/yr depending on coverage and structure."
                theme={theme}
              />
              <ImpactItem
                label="Permitting"
                detail="Local floodplain development permits required. Must demonstrate no rise in base flood elevation. Fill and grading require engineering certification."
                theme={theme}
              />
              <ImpactItem
                label="Construction Costs"
                detail="Structures must be elevated to or above BFE. Foundation costs increase 15\u201330%. Electrical and mechanical systems must be above flood level."
                theme={theme}
              />
              <ImpactItem
                label="Pad Placement"
                detail="For BTM data centers, pad sites should be located outside the SFHA where possible. If unavoidable, compensatory storage and no-rise certification add $50K\u2013$200K in engineering."
                theme={theme}
              />
              <ImpactItem
                label="Site Assembly"
                detail="Flood zones fragment usable acreage. A 2,500-acre site with 15% floodplain effectively has ~2,125 acres of developable land."
                theme={theme}
              />
            </div>
          ) : fz.flood_type === '500yr' ? (
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
              Mapped 0.2% annual-chance flood hazard, outside the 1% annual-chance SFHA. Consider this flood exposure when selecting pad elevations and evaluating site drainage.
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Undetermined flood risk.</strong> FEMA has not performed a detailed study of this area.
              </div>
              <div style={{ color: theme.textSecondary }}>
                A Letter of Map Amendment (LOMA) or site-specific survey may be needed to confirm flood status before development. Cost: $2K\u2013$10K for a survey + LOMA application.
              </div>
            </div>
          )}
        </Section>

        {/* Source info */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, marginBottom: '4px', color: theme.textSecondary }}>
              <HelpCircle size={10} /> Source
            </div>
            <div>{pending ? `FEMA pending county mapping · ${fz.product_id || ''}` : 'FEMA National Flood Hazard Layer (NFHL)'}</div>
            <div>Layer: S_FLD_HAZ_AR (Special Flood Hazard Areas)</div>
            <div style={{ marginTop: '6px' }}>
              <a
                href={pending ? fz.source_url : 'https://www.fema.gov/flood-maps/national-flood-hazard-layer'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: designTokens.colors.brand, textDecoration: 'underline', fontSize: '10px' }}
              >
                {pending ? 'FEMA source product' : 'FEMA NFHL Documentation'}
              </a>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
